import {
  AuthError,
  getAuthData,
  getCompanyId,
  logger,
  refreshAccessToken,
  singletonQueryClient,
} from '$utils'
import { QueryOptions } from '@tanstack/react-query'
import { errorHandler } from './helper'
import { AccessType } from '$constants'

/**
 * The default headers (bearer token, and the active company where one exists)
 * sent with every request made through this client. Exported for the rare
 * cases that need a hand-rolled fetch (e.g. per-request header overrides or
 * polling that must bypass the React Query caching in `get`) so auth/company
 * header construction still lives in one place.
 */
export const getDefaultHeaders = () => {
  const authData = getAuthData()
  let defaultHeaders: object = {
    authorization: `Bearer ${authData.accessToken}`,
  }

  // We don't want to include the company header for standalone pages
  if (authData?.accessType !== AccessType.Standalone) {
    const companyId = getCompanyId()

    // A user mid-signup has no company yet - omit the header rather than
    // sending a bogus value (matching AngularJS, which only sets its header
    // default once a company becomes active).
    if (companyId) {
      defaultHeaders = {
        ...defaultHeaders,
        'x-loaded-company-id': companyId,
      }
    }
  }

  return defaultHeaders
}

/**
 * Fetch wrapper that handles token refresh on 403 and common error responses.
 * Accepts a builder function for the RequestInit so that headers (including
 * the Bearer token) are reconstructed fresh on a retry.
 */
const fetchWithTokenRetry = async (
  apiUrl: string,
  path: string,
  buildInit: () => RequestInit
): Promise<Response> => {
  // Normalise the path and remove a leading slash, we don't want to end up
  // with two of these when constructing the url as the API won't tolerate
  // these, instead returning a 404.
  const trimmedPath = path.replace(/^\//, '')
  const url = `${apiUrl}/${trimmedPath}`

  let response = await fetch(url, buildInit())

  if (response.status === 403) {
    // The access token may have expired or be invalid for some other reason (
    // e.g. being added to a new venue since the last login). Refresh it and
    // retry once. buildInit() is called again so the retry picks up the new token.
    await refreshAccessToken()
    response = await fetch(url, buildInit())
  }

  if (!response.ok) {
    logger.error(`received HTTP ${response.status} from ${path}`)

    if (response.status === 401) {
      throw new AuthError('API client is not authenticated')
    }

    await errorHandler(response)
  }

  return response
}

/**
 * Execute a GET query against a given API.
 *
 * @param apiUrl the base url of the API
 * @param path the path of endpoint
 * @param options QueryOptions from React Query (without queryKey and queryFn)
 * @param signal to be able to abort the api call
 * @returns the data parsed from the JSON response received
 */
async function get<T>(
  apiUrl: string,
  path: string,
  options?: Omit<QueryOptions<T>, 'queryKey' | 'queryFn'>,
  signal?: AbortSignal
) {
  return singletonQueryClient.fetchQuery<T>({
    ...options,
    queryKey: [apiUrl, path, signal],
    // Token retry is handled inline via fetchWithTokenRetry rather than by
    // recursively calling get(), because re-entering fetchQuery with the
    // same queryKey would return the already-in-flight promise — deadlocking.
    queryFn: async () => {
      const response = await fetchWithTokenRetry(apiUrl, path, () => ({
        signal,
        headers: { ...getDefaultHeaders() },
      }))

      return response.json()
    },
  })
}

/**
 * Execute a POST against a given API.
 *
 * @param apiUrl the base url of the API
 * @param path the path of the endpoint
 * @param requestBody the body of the POST request
 * @param signal to be able to abort the api call
 * @returns the data parsed from the JSON response received
 */
async function add(
  apiUrl: string,
  path: string,
  requestBody: object | FormData,
  signal?: AbortSignal
) {
  // Normalise the path and remove a leading slash, we don't want to end up
  // with two of these when constructing the url as the API won't tolerate
  // these, instead returning a 404.
  const trimmedPath = path.replace(/^\//, '')

  const defaultHeaders = getDefaultHeaders()

  const headers: HeadersInit = {
    ...defaultHeaders,
    Accept: 'application/json',
  }

  if (requestBody instanceof FormData) {
    delete headers['Content-Type']
  } else {
    headers['Content-Type'] = 'application/json;charset=utf-8'
  }

  const response = await fetch(`${apiUrl}/${trimmedPath}`, {
    signal: signal,
    method: 'POST',
    headers: headers,
    body:
      requestBody instanceof FormData
        ? requestBody
        : JSON.stringify(requestBody),
  })

  if (!response.ok) {
    logger.error(`received HTTP ${response.status} sending data to ${path}`)

    if (response.status === 401) {
      throw new AuthError('API client is not authenticated')
    }

    return errorHandler(response)
  }
  const contentType = response.headers.get('Content-Type')
  if (contentType?.includes('application/json')) {
    return response.json()
  }
  return
}

/**
 * Execute a PUT against a given API.
 *
 * @param apiUrl the base url of the API
 * @param path the path of the endpoint
 * @param requestBody the body of the PUT request
 * @param signal to be able to abort the api call
 * @returns the data parsed from the JSON response received
 */
async function update(
  apiUrl: string,
  path: string,
  requestBody: object,
  signal?: AbortSignal
) {
  // Normalise the path and remove a leading slash, we don't want to end up
  // with two of these when constructing the url as the API won't tolerate
  // these, instead returning a 404.
  const trimmedPath = path.replace(/^\//, '')

  const defaultHeaders = getDefaultHeaders()

  const response = await fetch(`${apiUrl}/${trimmedPath}`, {
    signal: signal,
    method: 'PUT',
    headers: {
      ...defaultHeaders,
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=utf-8',
    },
    body: JSON.stringify(requestBody),
  })
  if (!response.ok) {
    logger.error(`received HTTP ${response.status} sending data to ${path}`)

    if (response.status === 401) {
      throw new AuthError('API client is not authenticated')
    }

    return errorHandler(response)
  }
  return response.status === 204 ? '' : response.json()
}

/**
 * Execute a DELETE agaisnt a given API.
 *
 * @param apiUrl the base url of the API
 * @param path the path of the endpoint
 * @param requestBody the body of the DELETE request
 * @param signal to be able to abort the api call
 * @returns the data parsed from the JSON response received
 */
async function remove(
  apiUrl: string,
  path: string,
  requestBody?: object,
  signal?: AbortSignal
) {
  // Normalise the path and remove a leading slash, we don't want to end up
  // with two of these when constructing the url as the API won't tolerate
  // these, instead returning a 404.
  const trimmedPath = path.replace(/^\//, '')

  const defaultHeaders = getDefaultHeaders()

  const response = await fetch(`${apiUrl}/${trimmedPath}`, {
    signal: signal,
    method: 'DELETE',
    headers: {
      ...defaultHeaders,
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=utf-8',
    },
    body: JSON.stringify(requestBody),
  })
  if (!response.ok) {
    logger.error(`received HTTP ${response.status} sending data to ${path}`)

    if (response.status === 401) {
      throw new AuthError('API client is not authenticated')
    }

    return errorHandler(response)
  }
  return response.status === 204 ? '' : response.json()
}

/**
 * Execute a PATCH against a given API.
 *
 * @param apiUrl the base url of the API
 * @param path the path of the endpoint
 * @param requestBody the body of the PATCH request
 * @param signal to be able to abort the api call
 * @returns the data parsed from the JSON response received
 */
async function patch(
  apiUrl: string,
  path: string,
  requestBody: object,
  signal?: AbortSignal
) {
  // Normalise the path and remove a leading slash, we don't want to end up
  // with two of these when constructing the url as the API won't tolerate
  // these, instead returning a 404.
  const trimmedPath = path.replace(/^\//, '')

  const defaultHeaders = getDefaultHeaders()

  const response = await fetch(`${apiUrl}/${trimmedPath}`, {
    signal: signal,
    method: 'PATCH',
    headers: {
      ...defaultHeaders,
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=utf-8',
    },
    body: JSON.stringify(requestBody),
  })
  if (!response.ok) {
    logger.error(`received HTTP ${response.status} sending data to ${path}`)

    if (response.status === 401) {
      throw new AuthError('API client is not authenticated')
    }

    return errorHandler(response)
  }
  return response.status === 204 ? '' : response.json()
}

export default {
  get,
  add,
  update,
  remove,
  patch,
}
